import { useEffect, useRef, useCallback } from "react";

/**
 * Custom hook to create a sticky horizontal scrollbar that stays at the bottom of the viewport
 * and syncs with the main table's horizontal scroll position.
 */
export const useStickyHorizontalScroll = (
  isDynamic: boolean,
  isEnabled: boolean = true,
) => {
  const mainScrollRef = useRef<HTMLDivElement>(null);
  const stickyScrollRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const isMainScrolling = useRef(false);
  const isStickyScrolling = useRef(false);

  // Sync scroll positions
  const syncScrollPositions = useCallback(() => {
    if (!mainScrollRef.current || !stickyScrollRef.current) return;

    const mainElement = mainScrollRef.current;
    const stickyElement = stickyScrollRef.current;

    // Update sticky scrollbar content width to match main content
    if (contentRef.current) {
      const mainScrollWidth = mainElement.scrollWidth;
      contentRef.current.style.width = `${mainScrollWidth}px`;
    }
  }, []);

  // Handle main scroll changes
  const handleMainScroll = useCallback(() => {
    if (
      !mainScrollRef.current ||
      !stickyScrollRef.current ||
      isStickyScrolling.current
    )
      return;

    isMainScrolling.current = true;
    stickyScrollRef.current.scrollLeft = mainScrollRef.current.scrollLeft;

    requestAnimationFrame(() => {
      isMainScrolling.current = false;
    });
  }, []);

  // Handle sticky scroll changes
  const handleStickyScroll = useCallback(() => {
    if (
      !mainScrollRef.current ||
      !stickyScrollRef.current ||
      isMainScrolling.current
    )
      return;

    isStickyScrolling.current = true;
    mainScrollRef.current.scrollLeft = stickyScrollRef.current.scrollLeft;

    requestAnimationFrame(() => {
      isStickyScrolling.current = false;
    });
  }, []);

  // Handle wheel events for horizontal scrolling
  const handleWheel = useCallback((e: WheelEvent) => {
    if (!mainScrollRef.current) return;
    
    // If shift is held or trackpad horizontal gesture
    if (e.shiftKey || Math.abs(e.deltaX) > Math.abs(e.deltaY)) {
      e.preventDefault();
      const scrollAmount = e.deltaX !== 0 ? e.deltaX : e.deltaY;
      mainScrollRef.current.scrollLeft += scrollAmount;
    }
  }, []);

  useEffect(() => {
    if (!isDynamic || !isEnabled) return;

    const mainElement = mainScrollRef.current;
    const stickyElement = stickyScrollRef.current;

    if (!mainElement || !stickyElement) return;

    // Initial sync
    syncScrollPositions();

    // Set up event listeners
    mainElement.addEventListener("scroll", handleMainScroll);
    stickyElement.addEventListener("scroll", handleStickyScroll);
    mainElement.addEventListener("wheel", handleWheel, { passive: false });

    // Sync on resize or content changes
    const resizeObserver = new ResizeObserver(syncScrollPositions);
    resizeObserver.observe(mainElement);

    // Cleanup
    return () => {
      mainElement.removeEventListener("scroll", handleMainScroll);
      stickyElement.removeEventListener("scroll", handleStickyScroll);
      mainElement.removeEventListener("wheel", handleWheel);
      resizeObserver.disconnect();
    };
  }, [
    isDynamic,
    isEnabled,
    handleMainScroll,
    handleStickyScroll,
    handleWheel,
    syncScrollPositions,
  ]);

  // Update content width when table changes
  const updateContentWidth = useCallback(() => {
    if (!isDynamic || !isEnabled) return;

    requestAnimationFrame(() => {
      syncScrollPositions();
    });
  }, [isDynamic, isEnabled, syncScrollPositions]);

  return {
    mainScrollRef,
    stickyScrollRef,
    contentRef,
    updateContentWidth,
  };
};
